import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  PTB_CARD_ANCHOR_Z_INDEX,
  computeCardAnchoredPosition,
  getCardElement,
} from './ptbCardAnchorUtils';

export const PTB_PHASE_MOVE_CONFIRM_MAX_WIDTH = 360;

function PhaseMoveConfirmPopover({
  isOpen = false,
  cardId = null,
  petName = '',
  toPhase = '',
  onConfirm,
  onCancel,
  isMoving = false,
  scrollContainerRef = null,
  preferredPlacement = 'above',
}) {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    placement: 'above',
    caretLeft: 0,
  });

  const updatePosition = useCallback(() => {
    if (!isOpen || !cardId) {
      return;
    }

    const cardEl = getCardElement(cardId);
    const popoverEl = popoverRef.current;

    if (!cardEl || !popoverEl) {
      return;
    }

  const nextPosition = computeCardAnchoredPosition(
    cardEl.getBoundingClientRect(),
    popoverEl.getBoundingClientRect(),
    { horizontalAlign: 'left', preferredPlacement },
  );

  setPosition(nextPosition);
}, [cardId, isOpen, preferredPlacement]);

  useLayoutEffect(() => {
    if (!isOpen || !cardId) {
      return undefined;
    }

    updatePosition();

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        updatePosition();
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [isOpen, cardId, petName, toPhase, updatePosition]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleScrollOrResize = () => {
      updatePosition();
    };

    window.addEventListener('resize', handleScrollOrResize);

    const scrollEl = scrollContainerRef?.current;
    scrollEl?.addEventListener('scroll', handleScrollOrResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleScrollOrResize);
      scrollEl?.removeEventListener('scroll', handleScrollOrResize);
    };
  }, [isOpen, scrollContainerRef, updatePosition]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleMouseDown = (event) => {
      if (popoverRef.current?.contains(event.target)) {
        return;
      }

      onCancel?.();
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      onCancel?.();
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onCancel]);

  if (!isOpen || !cardId) {
    return null;
  }

  const messageId = 'ptb-phase-move-confirm-message';

  return createPortal(
    <div
      ref={popoverRef}
      className={`ptb-phase-move-confirm ptb-phase-move-confirm--${position.placement}`}
      style={{
        top: position.top,
        left: position.left,
        zIndex: PTB_CARD_ANCHOR_Z_INDEX,
        '--ptb-phase-move-confirm-caret-left': `${position.caretLeft}px`,
      }}
      role="dialog"
      aria-labelledby={messageId}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <span className="ptb-phase-move-confirm__caret" aria-hidden="true" />

      <p id={messageId} className="ptb-phase-move-confirm__message">
        Move{' '}
        <span className="ptb-phase-move-confirm__pet-name" title={petName}>
          {petName}
        </span>{' '}
        to {toPhase}?
      </p>

      <div className="ptb-phase-move-confirm__actions">
        <button
          type="button"
          className="btn btn-utility ptb-phase-move-confirm__btn ptb-phase-move-confirm__btn--cancel"
          onClick={onCancel}
          disabled={isMoving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn ptb-phase-move-confirm__btn ptb-phase-move-confirm__btn--move"
          onClick={onConfirm}
          disabled={isMoving}
        >
          Move
        </button>
      </div>
    </div>,
    document.body,
  );
}

export default PhaseMoveConfirmPopover;
